import React, { useState, useEffect } from "react";

import {
  Grid,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  TextField,
  Divider,
  Box,
  CircularProgress,
  Card,
  Checkbox,
} from "@mui/material";

import RegularTemplateEditor from "./RegularTemplateEditor";
import MessageNode from "./MessageNode";
import CarouselTemplateEditor from "./CarouselTemplateEditor";

import axios from "axios";

import { TEMPLATE_CREATE_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";

import { TEMPLATE_ENDPOINT } from "../../config/urls";
import CatalogTemplateEditor from "./CatalogTemplateEditor";
import { ColorsConstants } from "../../cssUtils/constants";
import ButtonThree from "../../common/third-button";
import MultipleProductsTemplateEditor from "./MultipleProductsTemplateEditor";
import SingleProductTemplateEditor from "./SingleProductTemplateEditor";
import typography from "../../cssUtils/typography";
import { useUnsavedChanges, clearUnsavedChanges } from "../../common/unsaved-changes";
import AuthTemplateEditor from "./AuthTemplateEditor";
import {
  AUTH_NAME_ERROR,
  AUTH_TEMPLATE,
  buildAuthPayload,
  getAuthMissing,
} from "./authTemplateUtils";

const MPM_PRODUCT_LIMIT = 30;
const BUTTON_COMBINATION_ERROR =
  "Button combinations must be same across all cards";

const SetupTemplate = (props) => {
  const { getValidToken } = useAuth();
  const trackChanges = useUnsavedChanges();
  const [templateType, setTemplateType] = useState("marketing-template");
  const [marketingOption, setMarketingOption] = useState("custom");
  const [utilityOption, setUtilityOption] = useState("custom");

  const [loading, setLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const [headerDuplicatePlaceholderError, setHeaderDuplicatePlaceholderError] =
    useState(false);
  const [bodyDuplicatePlaceholderError, setBodyDuplicatePlaceholderError] =
    useState(false);

  const [
    carouselMainBodyDuplicatePlaceholderError,
    setCarouselMainBodyDuplicatePlaceholderError,
  ] = useState(false);
  const [
    carouselCardBodyDuplicatePlaceholderError,
    setCarouselCardBodyDuplicatePlaceholderError,
  ] = useState([]);

  // const { templatesNamesData, setComponent } = props;
  const { templatesNamesData: propTemplateNamesData, setComponent } = props;
  const [templatesNamesData, setTemplatesNamesData] = useState(
    propTemplateNamesData || [],
  );

  const [carouselType, setCarouselType] = useState("image");
  const [carouselMainBodySampleFields, setCarouselMainBodySampleFields] =
    useState([]);

  // State
  const [headerTextError, setHeaderTextError] = useState("");

  const columns = [
    { id: "header", label: "Header" },
    { id: "body", label: "Body" },
    { id: "footer", label: "Footer" },
  ];

  // edit template form
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("english");
  const [headerType, setHeaderType] = useState("none");
  const [headerText, setHeaderText] = useState("");
  const [headerFile, setHeaderFile] = useState(null);
  const [body, setBody] = useState("");
  const [sampleBodyFields, setsampleBodyFields] = useState([]);
  const [sampleHeaderFields, setsampleHeaderFields] = useState([]);
  const [footer, setFooter] = useState("");
  const [allButtons, setAllButtons] = useState([]);
  const [isButtonsValid, setIsButtonsValid] = useState(true);
  const [error, setError] = useState(false);
  const [templateNameError, setTemplateNameError] = useState("");
  const [location, setLocation] = useState({});
  const [locationLatitudeError, setLocationLatitudeError] = useState(false);
  const [locationLongitudeError, setLocationLongitudeError] = useState(false);
  const [templateTracking, setTemplateTracking] = useState(false);

  // Authentication template state (owned by AuthTemplateEditor, mirrored here for submit)
  const [authData, setAuthData] = useState(null);
  const isAuth = templateType === AUTH_TEMPLATE;
  const nameError =
    templateNameError || (isAuth && templateName.includes("-") ? AUTH_NAME_ERROR : "");
  const authMissing = isAuth
    ? getAuthMissing(authData, templateName, nameError || (error ? "duplicate" : ""))
    : [];

  // Multiple products template state
  const createMpmSection = (id) => ({
    id,
    title: `Section ${id}`,
    products: [],
  });

  const createMpmProduct = (id) => ({
    id,
    title: `Product ${id}`,
    value: "",
    retailerId: "",
  });

  const [mpmSections, setMpmSections] = useState([createMpmSection(1)]);
  const [mpmThumbnail, setMpmThumbnail] = useState(null);

  // Single product template state
  const [spmProduct, setSpmProduct] = useState(null);

  // Carousel template state
  const [carouselMainBody, setCarouselMainBody] = useState("");
  const [carouselCards, setCarouselCards] = useState([
    {
      id: Date.now(), // Add a unique ID to help React distinguish between cards
      // headerType: 'IMAGE',
      headerType: carouselType.toUpperCase(),
      headerFile: null,
      body: "",
      sampleBodyFields: [],
      buttons: [
        {
          type: "QUICK_REPLY",
          data: {
            buttonText: "",
          },
        },
      ],
    },
  ]);

  const [formData, setFormData] = useState({
    templateName: "",
    language: "",
    headerType: "",
    headerText: "",
    headerFile: "",
    sampleHeaderFields: [],
    body: "",
    sampleBodyFields: [],
    footer: "",
    marketingOption: "",
    templateType: "",
    allButtons: [],
    carouselMainBodySampleFields: [],
    templateTracking: false,
    mpmSections: [],
    mpmThumbnail: null,
    spmProduct: null,
  });

  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
    statusCode: "",
  });

  const endsWithParameter = (text) => {
    if (!text) return false;

    const s = typeof text === "string" ? text : String(text);

    // Last non-empty line
    const lines = s.split(/\r?\n/).filter((line) => line.trim() !== "");
    if (lines.length === 0) return false;

    const lastLine = lines[lines.length - 1];

    // Rule: invalid if last line ends with {{number}} followed only by whitespace/punctuation
    return /\{\{\d+\}\}[\s\p{P}]*$/u.test(lastLine);
  };

  const handleSuccessAlert = (msg = "Template submitted successfully") => {
    setAlertObj({
      active: true,
      type: "success",
      msg,
      statusCode: 201,
    });
    clearUnsavedChanges();
    setTimeout(() => {
      // check if activePage in localstorage is '14' (Whatsapp)
      if (localStorage.getItem("activePage") === "view_templates") {
        setComponent(1);
      } else {
        props.onNavigate("view_templates", { refreshKey: Date.now() }); // Navigate back to Whatsapp with a new key
      }
    }, 1500);
  };

  const handleErrorAlert = (statusCode, responseText) => {
    setAlertObj({
      active: true,
      type: "error",
      msg: `Failed to submit template: ${responseText}`,
      statusCode: statusCode,
    });
  };

  const handleAlertClose = () => {
    setAlertObj({
      active: false,
      type: "",
      msg: "",
      statusCode: "",
    });
  };

  useEffect(() => {
    const nextFormData = {
      templateName,
      language,
      headerType,
      headerText,
      headerFile,
      sampleHeaderFields,
      body,
      sampleBodyFields,
      footer,
      marketingOption,
      templateType,
      allButtons,
      carouselCards,
      carouselMainBody,
      carouselMainBodySampleFields,
      carouselType,
      location,
      templateTracking,
      mpmSections,
      mpmThumbnail,
      spmProduct,
    };
    setFormData(nextFormData);
    // First run holds the empty form; any later difference is an unsaved edit
    trackChanges(nextFormData);
  }, [
    templateName,
    language,
    headerType,
    headerText,
    headerFile,
    sampleHeaderFields,
    body,
    sampleBodyFields,
    footer,
    marketingOption,
    templateType,
    allButtons,
    carouselCards,
    carouselMainBody,
    carouselMainBodySampleFields,
    carouselType,
    location,
    templateName,
    templateTracking,
    mpmSections,
    mpmThumbnail,
    spmProduct,
  ]);

  useEffect(() => {
    // If already provided by props and not empty, no need to fetch
    if (propTemplateNamesData && propTemplateNamesData.length > 0) {
      setTemplatesNamesData(propTemplateNamesData);
      return;
    }

    const fetchTemplateNames = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");

        const res = await fetch(`${TEMPLATE_ENDPOINT}?limit=1000`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });

        if (!res.ok) throw new Error("Failed to fetch template names");

        const data = await res.json();
        setTemplatesNamesData(data.template_names || []);
        console.log("Fetched template names:", templatesNamesData);
      } catch (error) {
        console.error("Error fetching template names:", error);
      }
    };

    fetchTemplateNames();
  }, [propTemplateNamesData]);

  const handleSubmitData = async (event) => {
    const apiUrl = TEMPLATE_CREATE_ENDPOINT;

    // Prevent submit if validation errors
    if (
      headerTextError || // NEW check
      headerDuplicatePlaceholderError ||
      bodyDuplicatePlaceholderError ||
      carouselMainBodyDuplicatePlaceholderError ||
      carouselCardBodyDuplicatePlaceholderError.some(Boolean) ||
      locationLatitudeError ||
      locationLongitudeError
    ) {
      setAlertObj({
        active: true,
        type: "error",
        msg:
          headerTextError || "Please resolve the parameter errors in the form",
      });
      return;
    }

    if (
      endsWithParameter(formData.body) ||
      endsWithParameter(formData.headerText) ||
      endsWithParameter(formData.footer) ||
      endsWithParameter(formData.carouselMainBodySampleFields)
    ) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "A template cannot end with a parameter e.g., '{{1}}'. Please add text after it.",
      });
      return;
    }

    // If carousel, validate each card
    if (
      formData.marketingOption === "carousel" &&
      Array.isArray(formData.carouselCards)
    ) {
      for (let i = 0; i < formData.carouselCards.length; i++) {
        if (endsWithParameter(formData.carouselCards[i].body)) {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Carousel Card ${
              i + 1
            } body cannot end with a parameter (e.g., '{{1}}'). Please add at least one alphanumeric character after it.`,
          });
          return;
        }
      }
    }

    // Prevent submit if there are duplicate placeholder errors
    if (
      headerDuplicatePlaceholderError ||
      bodyDuplicatePlaceholderError ||
      carouselMainBodyDuplicatePlaceholderError ||
      carouselCardBodyDuplicatePlaceholderError.some(Boolean)
    ) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please resolve the parameter errors in the form",
      });
      return;
    }

    if (formData.marketingOption === "carousel") {
      // CAROUSEL TEMPLATE SPECIFIC VALIDATION

      if (formData.carouselCards.length === 0) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please add at least one card",
        });
        return;
      }

      // Check if main body is empty
      if (
        !formData.carouselMainBody ||
        formData.carouselMainBody.trim() === ""
      ) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please enter a main message body for the template",
        });
        return;
      }

      // Validate each card
      for (let i = 0; i < formData.carouselCards.length; i++) {
        const card = formData.carouselCards[i];
        if (!card.headerFile) {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Please add ${
              formData.carouselType === "video" ? "a video" : "an image"
            } for Card ${i + 1}`,
          });
          return;
        }

        // For video files, add additional validation if needed
        if (formData.carouselType === "video" && card.headerFile) {
          // Optional: Add video-specific validation (e.g., file size, duration)
          const fileSize = card.headerFile.size / (1024 * 1024); // Convert to MB
          if (fileSize > 16) {
            // WhatsApp typically limits videos to 16MB
            setAlertObj({
              active: true,
              type: "error",
              msg: `Video file for Card ${
                i + 1
              } exceeds the maximum size of 16MB`,
            });
            return;
          }
        }

        if (!card.body || card.body.trim() === "") {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Please add body text for Card ${i + 1}`,
          });
          return;
        }
        if (card.buttons.length === 0) {
          setAlertObj({
            active: true,
            type: "error",
            msg: `Please add at least one button for Card ${i + 1}`,
          });
          return;
        }
      }
    } else if (formData.marketingOption === "catalog") {
      // CATALOG TEMPLATE SPECIFIC VALIDATION

      if (!formData.body?.trim()) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Message body is required",
        });
        document.getElementById("template-body")?.focus();
        return;
      }

      formData.allButtons = [
        {
          type: "catalog",
          data: {
            buttonText: "View Catalog",
          },
        },
      ];
    } else if (formData.marketingOption === "single-product") {
      // SINGLE PRODUCT TEMPLATE SPECIFIC VALIDATION

      if (!formData.body?.trim()) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Message body is required",
        });
        document.getElementById("template-body")?.focus();
        return;
      }

      if (!formData?.spmProduct || !formData?.spmProduct?.retailer_id) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please select a product",
        });
        return;
      }
    } else if (formData.marketingOption === "multiple-products") {
      // MULTIPLE PRODUCTS TEMPLATE SPECIFIC VALIDATION

      if (!formData.body?.trim()) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Message body is required",
        });
        document.getElementById("template-body")?.focus();
        return;
      }

      if (formData?.mpmSections?.length === 0) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please add at least one section",
        });
        return;
      }

      const totalProducts = formData?.mpmSections?.reduce(
        (total, section) => total + section?.products?.length,
        0,
      );

      if (totalProducts < 1 || totalProducts > MPM_PRODUCT_LIMIT ) {
        setAlertObj({
          active: true,
          type: "error",
          msg: `Total number of products must be between 1 and ${MPM_PRODUCT_LIMIT}`,
        });
        return;
      }

      if (!formData?.mpmThumbnail || !formData?.mpmThumbnail?.retailer_id) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please select a thumbnail product",
        });
        return;
      }

      // Check for duplicate products
      const productIds = new Set();
      for (const section of formData.mpmSections) {
        for (const product of section.products) {
          if (productIds.has(product.retailerId)) {
            setAlertObj({
              active: true,
              type: "error",
              msg: "Duplicate product found in multiple sections",
            });
            return;
          }
          productIds.add(product.retailerId);
        }
      }

      if (!productIds.has(formData.mpmThumbnail.retailer_id)) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "The thumbnail product must also be added to one of the sections. Please add it to a section or choose a different thumbnail.",
        });
        return;
      }
    } else {
      // CUSTOM TEMPLATE SPECIFIC VALIDATION
      if (
        formData.headerType === "image" ||
        formData.headerType === "video" ||
        formData.headerType === "document" ||
        formData.headerType === "gif"
      ) {
        if (!formData.headerFile) {
          setAlertObj({
            active: true,
            type: "error",
            msg: "Please upload a media file",
          });
          return;
        }
      } else if (formData.headerType === "location") {
        if (!location) {
          setAlertObj({
            active: true,
            type: "error",
            msg: "Please enter a location",
          });
          return;
        }
      }
      if (!formData.body?.trim()) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Message body is required",
        });
        document.getElementById("template-body")?.focus();
        return;
      }
    }

    const data = { ...formData };
    data.sampleHeaderFields =
      data.sampleHeaderFields.length === 0
        ? ""
        : JSON.stringify(data.sampleHeaderFields);
    data.sampleBodyFields =
      data.sampleBodyFields.length === 0
        ? ""
        : JSON.stringify(data.sampleBodyFields);
    data.allButtons =
      data.allButtons.length === 0 ? "" : JSON.stringify(data.allButtons);

    // process carousel main body sample fields
    data.carouselMainBodySampleFields =
      data.carouselMainBodySampleFields?.length === 0
        ? ""
        : JSON.stringify(data.carouselMainBodySampleFields);

    data.location =
      location && Object.keys(location).length > 0
        ? JSON.stringify(location)
        : "";

    // process mpmSections
    data.mpmSections =
      data.mpmSections?.length === 0 ? "" : JSON.stringify(data.mpmSections);

    // process mpmThumbnail
    data.mpmThumbnail = data.mpmThumbnail
      ? JSON.stringify({
          title: data.mpmThumbnail.name,
          retailerId: data.mpmThumbnail.retailer_id,
          imageUrl: data.mpmThumbnail.image_url,
        })
      : "";

    // process spmProduct
    data.spmProduct = data.spmProduct
      ? JSON.stringify({
          title: data.spmProduct.name,
          retailerId: data.spmProduct.retailer_id,
          imageUrl: data.spmProduct.image_url,
        })
      : "";

    // Raise error if body parameter is present but is empty
    if (sampleBodyFields) {
      const invalidFieldIndex = sampleBodyFields.findIndex((field) => {
        if (!field || !field.type || !field.hasOwnProperty("value")) {
          return true;
        }

        const trimmedValue =
          typeof field.value === "string" ? field.value.trim() : field.value;

        if (
          trimmedValue === "" ||
          trimmedValue === null ||
          trimmedValue === undefined
        ) {
          return true;
        }

        if (field.type === "custom" && trimmedValue === "") {
          return true;
        }

        return false;
      });

      if (invalidFieldIndex !== -1) {
        setAlertObj({
          active: true,
          type: "error",
          msg: `Please provide a value for body variable {{${
            invalidFieldIndex + 1
          }}} before submitting.`,
        });
        return;
      }
    }

    console.log("Payload: ", data);

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    // Use FormData to handle file uploads properly
    const formDataObj = new FormData();

    // Add all regular fields to FormData
    for (const key in data) {
      if (key !== "headerFile" && key !== "carouselCards") {
        formDataObj.append(key, data[key]);
      }
    }

    // Handle carousel cards specially
    if (data.marketingOption === "carousel") {
      // First, create a simplified version of cards without the file objects
      const simplifiedCards = data.carouselCards.map((card) => {
        const { headerFile, ...rest } = card;
        return rest;
      });

      formDataObj.append("carouselCards", JSON.stringify(simplifiedCards));

      // Then append each card's file separately
      data.carouselCards.forEach((card, index) => {
        if (card.headerFile) {
          console.log(
            `Appending file for card ${index}:`,
            card.headerFile.name,
          );
          formDataObj.append(`cardFile_${index}`, card.headerFile);
        }
      });
    } else if (data.headerFile) {
      // Handle regular template file
      formDataObj.append("headerFile", data.headerFile);
    }

    // Log the FormData entries for debugging
    for (let pair of formDataObj.entries()) {
      if (pair[0].startsWith("cardFile_") || pair[0] === "headerFile") {
        console.log(`${pair[0]}: File object with name ${pair[1].name}`);
      } else {
        console.log(
          `${pair[0]}: ${
            typeof pair[1] === "string" && pair[1].length > 100
              ? pair[1].substring(0, 100) + "..."
              : pair[1]
          }`,
        );
      }
    }

    setLoading(true);
    console.log("Calling API...");

    try {
      const response = await axios.post(apiUrl, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": tenant,
        },
      });

      console.log("API Response:", response.data);
      if (response.status === 201) {
        handleSuccessAlert();
        // Disable submit button after successful submission
        setSubmitDisabled(true);
      } else {
        let message = response.data.message
          ? response.data.message
          : response.statusText;
        handleErrorAlert(response.status, message);
      }
    } catch (error) {
      console.error("API Error:", error);
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes(BUTTON_COMBINATION_ERROR)
      ) {
        handleErrorAlert(
          400,
          "Please ensure that all cards have the same button combination (e.g., Quick Reply, URL, Phone Number) and the same number of buttons.",
        );
      } else {
        setAlertObj({
          active: true,
          type: "error",
          msg: `Failed to submit form: ${
            error.response?.data?.message ||
            error.response?.statusText ||
            error.message
          }`,
          statusCode: error.response?.status || 500,
        });
      }
    } finally {
      setLoading(false); // This will run whether the request succeeds or fails
    }
  };

  const submitAuthTemplate = async () => {
    if (authMissing.length > 0) {
      handleErrorAlert(400, `To submit, add: ${authMissing.join(", ")}`);
      return;
    }

    const formDataObj = new FormData();
    const data = {
      templateName,
      language,
      templateType,
      ...buildAuthPayload(authData),
    };
    Object.entries(data).forEach(([key, value]) => formDataObj.append(key, value));

    setLoading(true);
    try {
      const token = await getValidToken();
      const response = await axios.post(TEMPLATE_CREATE_ENDPOINT, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          "X-Tenant-Id": localStorage.getItem("tenant"),
        },
      });
      if (response.status === 201) {
        handleSuccessAlert(`Submitted ${templateName} to Meta. Status: Pending.`);
        setSubmitDisabled(true);
      } else {
        handleErrorAlert(response.status, response.data.message || response.statusText);
      }
    } catch (err) {
      handleErrorAlert(
        err.response?.status || 500,
        err.response?.data?.message || err.response?.statusText || err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Check for duplicate name before doing anything else
    const isDuplicate = templatesNamesData.some(
      (name) => name.toLowerCase() === templateName.trim().toLowerCase(),
    );
    if (isDuplicate) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "A template with the same name already exists.",
      });
      return; // stop submission
    }

    if (isAuth) {
      submitAuthTemplate();
      return;
    }

    if (!isButtonsValid || error) {
      return;
    }

    // Submit the form data here
    setFormData({
      templateName,
      language,
      headerType,
      headerText,
      headerFile,
      sampleHeaderFields,
      body,
      sampleBodyFields,
      footer,
      marketingOption,
      templateType,
      allButtons,
      carouselCards,
      carouselMainBody,
      carouselMainBodySampleFields,
      location,
      templateTracking,
      mpmSections,
      mpmThumbnail,
      spmProduct,
    });
    handleSubmitData();
  };

  const handleMarketingOptionChange = (event) => {
    setMarketingOption(event.target.value);
    setUtilityOption("");
  };

  const handleUtilityOptionChange = (event) => {
    setUtilityOption(event.target.value);
    setMarketingOption("");
  };

  const handleTemplateNameChange = (event) => {
    const newTemplateName = event.target.value
      .toLowerCase()
      .replace(/\s+/g, "_");

    // Allow only letters, numbers, underscores, and hyphens
    const isValid = /^[a-z0-9_-]*$/.test(newTemplateName);

    if (!isValid) {
      setTemplateNameError(
        "Only letters, numbers, underscores, and hyphens allowed.",
      );
    } else {
      setTemplateNameError("");
    }
    setTemplateName(newTemplateName);
  };

  // Check for duplicate template name
  useEffect(() => {
    if (!templatesNamesData || !templatesNamesData.length) {
      return;
    }
    const isDuplicate = templatesNamesData.some(
      (name) => name.toLowerCase() === templateName.trim().toLowerCase(),
    );
    console.log(
      "Checking for duplicate template name:",
      templatesNamesData,
      "Found:",
      isDuplicate,
    );
    setError(isDuplicate);
  }, [templateName, templatesNamesData]);

  useEffect(() => {
    let timeoutId;

    if (alertObj.active) {
      timeoutId = setTimeout(() => {
        handleAlertClose();
      }, 5000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [alertObj]);

  useEffect(() => {
    if (carouselCards?.length > 0 && formData.marketingOption === "carousel") {
      const headerTypeValue = carouselType === "video" ? "VIDEO" : "IMAGE";

      const hasIncorrectTypes = carouselCards.some(
        (card) => card.headerType !== headerTypeValue,
      );

      if (hasIncorrectTypes) {
        const updatedCards = carouselCards.map((card) => ({
          ...card,
          headerType: headerTypeValue,
        }));

        setCarouselCards(updatedCards);
      }
    }
  }, [carouselType, carouselCards]);

  const openGuidelinesPdf = async () => {
    const response = await fetch(
      "https://app.mybotbridge.com/api/template_media/reference_guide_template_category.pdf",
      {
        method: "GET",
        headers: {
          "X-Tenant-ID": localStorage.getItem("tenant"),
        },
      },
    );

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    window.open(url, "_blank");
  };

  return (
    <div sx={{ padding: 2 }} style={{ marginTop: "12px"}}>
      <form
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
          mt: 3
        }}
      >
        <Stack spacing={1} sx={{ mt: { xs: 0 } }}>
          {/* Submit Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: {
                xs: "column", // mobile
                sm: "row", // tablet+
              },
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              backgroundColor: "#ffffff",
              py: 1.5,
              px: { xs: 1, sm: 0 },
              borderTop: `1px solid ${ColorsConstants.secondaryGray}`,
            }}
          >
            <Typography variant="body2" sx={{ my: 1 }}>
              <span
                onClick={openGuidelinesPdf}
                style={{
                  // textDecoration: "underline",
                  cursor: "pointer",
                  color: ColorsConstants.primaryBlue100,
                  fontWeight: "bold",
                }}
              >
                View Meta guidelines to create Marketing & Utility templates
              </span>
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
            {isAuth && authMissing.length > 0 && (
              <Typography
                variant="body2"
                sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray60 }}
              >
                To submit, add: {authMissing.join(", ")}
              </Typography>
            )}
            <ButtonThree
              sx={{
                textTransform: "none",
                backgroundColor: ColorsConstants.primaryBlue100,
                "&:hover": {
                  backgroundColor: ColorsConstants.primaryBlue100,
                },
                "&.Mui-disabled": {
                  backgroundColor: ColorsConstants.primaryBlue100,
                  color: "#ffffff",
                  opacity: 0.4,
                },
                px: 2,
                py: 1,
                color: "#ffffff",
              }}
              type="submit"
              disabled={
                loading ||
                submitDisabled ||
                !templateName.trim() ||
                Boolean(nameError) ||
                error ||
                (isAuth
                  ? authMissing.length > 0
                  : marketingOption === "carousel"
                    ? !carouselMainBody.trim()
                    : !body.trim())
              }
            >
              {loading ? (
                <span
                  style={{
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />{" "}
                  Submitting...
                </span>
              ) : (
                <span style={{ color: "white" }}>Submit For Approval</span>
              )}
            </ButtonThree>
            </Stack>
          </Box>

          {/* Alert */}
          {alertObj.active && (
            <Grid item xs={12}>
              <Alert severity={alertObj.type} onClose={handleAlertClose}>
                {alertObj.msg}
              </Alert>
            </Grid>
          )}
        </Stack>
        <Stack spacing={3} sx={{ mt: 3 }}>
          {/* Main Stack */}
          <Stack spacing={0.5} sx={{ width: "100%" }}>
            {/* Sub Stack */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: 2,
              }}
            >
              {/* Template Name */}
              <Tooltip title="Template name" arrow placement="top">
                <TextField
                  sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                  label="Template Name"
                  value={templateName}
                  onChange={handleTemplateNameChange}
                  InputProps={{
                    inputProps: {
                      maxLength: 512,
                    },
                  }}
                  error={error || Boolean(nameError)}
                  helperText={
                    nameError
                      ? nameError
                      : error
                        ? "Template name must be unique."
                        : ""
                  }
                  required
                />
              </Tooltip>

              {/* Category */}
              <FormControl
                sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
              >
                <InputLabel id="template-type-label">Category</InputLabel>
                <Select
                  labelId="template-type-label"
                  id="template-type"
                  name="template-type"
                  value={templateType}
                  label="Category"
                  onChange={(event) => {
                    // Leaving Authentication drops its data and restores the Custom editor
                    if (isAuth && event.target.value !== AUTH_TEMPLATE) {
                      setAuthData(null);
                      setMarketingOption("custom");
                      setUtilityOption("custom");
                    }
                    setTemplateType(event.target.value);
                  }}
                  defaultValue="marketing-template"
                >
                  <MenuItem value="marketing-template">Marketing</MenuItem>
                  <MenuItem value="utility-template">Utility</MenuItem>
                  <MenuItem value={AUTH_TEMPLATE}>Authentication</MenuItem>
                </Select>
              </FormControl>

              {/* Type */}
              <FormControl
                sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
              >
                <InputLabel id="marketing-option-label">Type</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={
                    isAuth
                      ? "template-library"
                      : templateType === "marketing-template"
                        ? marketingOption
                        : utilityOption
                  }
                  label="Type"
                  disabled={isAuth}
                  onChange={
                    templateType === "marketing-template"
                      ? handleMarketingOptionChange
                      : handleUtilityOptionChange
                  }
                >
                  {isAuth && (
                    <MenuItem value="template-library">Template Library</MenuItem>
                  )}
                  {!isAuth && <MenuItem value="custom">Custom</MenuItem>}
                  {templateType === "marketing-template" && (
                    <MenuItem value="carousel">Carousel</MenuItem>
                  )}
                  {templateType === "marketing-template" && (
                    <MenuItem value="catalog">Catalog</MenuItem>
                  )}
                  {templateType === "marketing-template" && (
                    <MenuItem value="single-product">Single Product</MenuItem>
                  )}
                  {templateType === "marketing-template" && (
                    <MenuItem value="multiple-products">
                      Multiple Products
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Language */}
              <Tooltip title="Language" arrow placement="top">
                <FormControl
                  sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                >
                  <InputLabel id="marketing-option-label">Language</InputLabel>
                  <Select
                    label="Language"
                    value={language}
                    onChange={(event) => setLanguage(event.target.value)}
                    required
                  >
                    <MenuItem value="english">English</MenuItem>
                    <MenuItem value="spanish">Spanish</MenuItem>
                    <MenuItem value="hindi">Hindi</MenuItem>
                    <MenuItem value="marathi">Marathi</MenuItem>
                    <MenuItem value="malayalam">Malayalam</MenuItem>
                    <MenuItem value="tamil">Tamil</MenuItem>
                    <MenuItem value="bengali">Bengali</MenuItem>
                    <MenuItem value="kannada">Kannada</MenuItem>
                    <MenuItem value="telugu">Telugu</MenuItem>
                    <MenuItem value="gujarati">Gujarati</MenuItem>
                    <MenuItem value="arabic">Arabic</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider />

          {/* Custom Template */}
          {((templateType === "marketing-template" &&
            marketingOption === "custom") ||
            (templateType === "utility-template" &&
              utilityOption === "custom")) && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{ flexDirection: { xs: "column", md: "row" } }}
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <RegularTemplateEditor
                  templateType={templateType}
                  headerType={headerType}
                  setHeaderType={setHeaderType}
                  headerText={headerText}
                  setHeaderText={setHeaderText}
                  headerFile={headerFile}
                  setHeaderFile={setHeaderFile}
                  body={body}
                  setBody={setBody}
                  sampleBodyFields={sampleBodyFields}
                  setsampleBodyFields={setsampleBodyFields}
                  sampleHeaderFields={sampleHeaderFields}
                  setsampleHeaderFields={setsampleHeaderFields}
                  footer={footer}
                  setFooter={setFooter}
                  allButtons={allButtons}
                  setAllButtons={setAllButtons}
                  isButtonsValid={isButtonsValid}
                  setIsButtonsValid={setIsButtonsValid}
                  headerDuplicatePlaceholderError={
                    headerDuplicatePlaceholderError
                  }
                  setHeaderDuplicatePlaceholderError={
                    setHeaderDuplicatePlaceholderError
                  }
                  bodyDuplicatePlaceholderError={bodyDuplicatePlaceholderError}
                  setBodyDuplicatePlaceholderError={
                    setBodyDuplicatePlaceholderError
                  }
                  headerTextError={headerTextError} // NEW
                  setHeaderTextError={setHeaderTextError} // NEW
                  location={location}
                  setLocation={setLocation}
                  locationLatitudeError={locationLatitudeError}
                  locationLongitudeError={locationLongitudeError}
                  setLocationLatitudeError={setLocationLatitudeError}
                  setLocationLongitudeError={setLocationLongitudeError}
                />
              </Box>

              {/* Preview */}
              <MessageNode
                templateName={templateName}
                language={language}
                headerType={headerType}
                headerText={headerText}
                headerFile={headerFile}
                body={body}
                sampleBodyFields={sampleBodyFields}
                sampleHeaderFields={sampleHeaderFields}
                footer={footer}
                allButtons={allButtons}
                showSampleValues={true} // Use regular preview mode
                location={location}
                mode="edit"
              />
            </Stack>
          )}

          {/* Carousel Template */}
          {templateType === "marketing-template" &&
            marketingOption === "carousel" && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ flexDirection: { xs: "column", md: "row" } }}
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <CarouselTemplateEditor
                    templateName={templateName}
                    setTemplateName={setTemplateName}
                    language={language}
                    setLanguage={setLanguage}
                    cards={carouselCards}
                    setCards={setCarouselCards}
                    mainBody={carouselMainBody}
                    setMainBody={setCarouselMainBody}
                    mainBodySampleFields={carouselMainBodySampleFields}
                    setMainBodySampleFields={setCarouselMainBodySampleFields}
                    error={error}
                    setError={setError}
                    templatesNamesData={templatesNamesData}
                    carouselType={carouselType}
                    setCarouselType={setCarouselType}
                    carouselMainBodyDuplicatePlaceholderError={
                      carouselMainBodyDuplicatePlaceholderError
                    }
                    setCarouselMainBodyDuplicatePlaceholderError={
                      setCarouselMainBodyDuplicatePlaceholderError
                    }
                    carouselCardBodyDuplicatePlaceholderError={
                      carouselCardBodyDuplicatePlaceholderError
                    }
                    setCarouselCardBodyDuplicatePlaceholderError={
                      setCarouselCardBodyDuplicatePlaceholderError
                    }
                  />
                </Box>

                {/* Preview */}
                <MessageNode
                  templateName={templateName}
                  language={language}
                  headerType="none"
                  headerText=""
                  headerFile={null}
                  body=""
                  sampleBodyFields={[]}
                  sampleHeaderFields={[]}
                  footer=""
                  allButtons={[]}
                  showSampleValues={false}
                  mode="edit"
                  marketingOption="carousel"
                  carouselCards={carouselCards}
                  carouselMainBody={carouselMainBody}
                  carouselMainBodySampleFields={carouselMainBodySampleFields}
                  carouselType={carouselType}
                  {...carouselCards.reduce((acc, card, index) => {
                    if (card.headerFile) {
                      acc[`cardFile_${index}`] = card.headerFile;
                    }
                    return acc;
                  }, {})}
                />
              </Stack>
            )}

          {/* Catalog Template */}
          {templateType === "marketing-template" &&
            marketingOption === "catalog" && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ flexDirection: { xs: "column", md: "row" } }}
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <CatalogTemplateEditor
                    templateType={templateType}
                    headerType={headerType}
                    setHeaderType={setHeaderType}
                    headerText={headerText}
                    setHeaderText={setHeaderText}
                    headerFile={headerFile}
                    setHeaderFile={setHeaderFile}
                    body={body}
                    setBody={setBody}
                    sampleBodyFields={sampleBodyFields}
                    setsampleBodyFields={setsampleBodyFields}
                    sampleHeaderFields={sampleHeaderFields}
                    setsampleHeaderFields={setsampleHeaderFields}
                    footer={footer}
                    setFooter={setFooter}
                    allButtons={allButtons}
                    setAllButtons={setAllButtons}
                    isButtonsValid={isButtonsValid}
                    setIsButtonsValid={setIsButtonsValid}
                    headerDuplicatePlaceholderError={
                      headerDuplicatePlaceholderError
                    }
                    setHeaderDuplicatePlaceholderError={
                      setHeaderDuplicatePlaceholderError
                    }
                    bodyDuplicatePlaceholderError={
                      bodyDuplicatePlaceholderError
                    }
                    setBodyDuplicatePlaceholderError={
                      setBodyDuplicatePlaceholderError
                    }
                    headerTextError={headerTextError} // NEW
                    setHeaderTextError={setHeaderTextError} // NEW
                    location={location}
                    setLocation={setLocation}
                    locationLatitudeError={locationLatitudeError}
                    locationLongitudeError={locationLongitudeError}
                    setLocationLatitudeError={setLocationLatitudeError}
                    setLocationLongitudeError={setLocationLongitudeError}
                  />
                </Box>

                {/* Preview */}
                <MessageNode
                  templateName={templateName}
                  marketingOption="catalog"
                  language={language}
                  headerType={headerType}
                  headerText={headerText}
                  headerFile={headerFile}
                  body={body}
                  sampleBodyFields={sampleBodyFields}
                  sampleHeaderFields={sampleHeaderFields}
                  footer={footer}
                  showSampleValues={true} // Use regular preview mode
                  location={location}
                  mode="edit"
                />
              </Stack>
            )}

          {/* Single Product Template */}
          {templateType === "marketing-template" &&
            marketingOption === "single-product" && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ flexDirection: { xs: "column", md: "row" } }}
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <SingleProductTemplateEditor
                    body={body}
                    setBody={setBody}
                    product={spmProduct}
                    setProduct={setSpmProduct}
                    sampleBodyFields={sampleBodyFields}
                    setsampleBodyFields={setsampleBodyFields}
                    footer={footer}
                    setFooter={setFooter}
                    bodyDuplicatePlaceholderError={
                      bodyDuplicatePlaceholderError
                    }
                    setBodyDuplicatePlaceholderError={
                      setBodyDuplicatePlaceholderError
                    }
                  />
                </Box>

                {/* Preview */}
                <MessageNode
                  templateName={templateName}
                  marketingOption="single-product"
                  language={language}
                  headerType="none"
                  headerText=""
                  headerFile={null}
                  body={body}
                  sampleBodyFields={sampleBodyFields}
                  sampleHeaderFields={sampleHeaderFields}
                  footer={footer}
                  showSampleValues={true}
                  location={location}
                  mode="edit"
                  thumbnailImage={spmProduct?.image_url}
                  productName={spmProduct?.name}
                />
              </Stack>
            )}

          {/* Multiple Products Template */}
          {templateType === "marketing-template" &&
            marketingOption === "multiple-products" && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{ flexDirection: { xs: "column", md: "row" } }}
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <MultipleProductsTemplateEditor
                    headerType={headerType}
                    setHeaderType={setHeaderType}
                    headerText={headerText}
                    setHeaderText={setHeaderText}
                    body={body}
                    setBody={setBody}
                    sections={mpmSections}
                    setSections={setMpmSections}
                    thumbnail={mpmThumbnail}
                    setThumbnail={setMpmThumbnail}
                    createSection={createMpmSection}
                    createProduct={createMpmProduct}
                    sampleBodyFields={sampleBodyFields}
                    setsampleBodyFields={setsampleBodyFields}
                    sampleHeaderFields={sampleHeaderFields}
                    setsampleHeaderFields={setsampleHeaderFields}
                    footer={footer}
                    setFooter={setFooter}
                    headerDuplicatePlaceholderError={
                      headerDuplicatePlaceholderError
                    }
                    setHeaderDuplicatePlaceholderError={
                      setHeaderDuplicatePlaceholderError
                    }
                    bodyDuplicatePlaceholderError={
                      bodyDuplicatePlaceholderError
                    }
                    setBodyDuplicatePlaceholderError={
                      setBodyDuplicatePlaceholderError
                    }
                    headerTextError={headerTextError} // NEW
                    setHeaderTextError={setHeaderTextError} // NEW
                  />
                </Box>

                {/* Preview */}
                <MessageNode
                  templateName={templateName}
                  marketingOption="multiple-products"
                  language={language}
                  headerType={headerType}
                  headerText={headerText}
                  headerFile={headerFile}
                  body={body}
                  sampleBodyFields={sampleBodyFields}
                  sampleHeaderFields={sampleHeaderFields}
                  footer={footer}
                  showSampleValues={true} // Use regular preview mode
                  location={location}
                  mode="edit"
                  productCount={mpmSections.reduce(
                    (count, section) => count + section.products.length,
                    0,
                  )}
                  thumbnailImage={mpmThumbnail?.image_url}
                />
              </Stack>
            )}
          {/* Authentication Template */}
          {isAuth && (
            <AuthTemplateEditor
              templateName={templateName}
              language={language}
              onChange={setAuthData}
            />
          )}

          {!isAuth && allButtons && allButtons.length > 0 && (
            <Box>
              <FormControlLabel
                sx={{ ...typography.h2, fontWeight: 500}}
                control={
                  <Checkbox
                    checked={templateTracking}
                    onChange={(e) => setTemplateTracking(e.target.checked)}
                  />
                }
                label="Update Audience Tag with button response"
              />
            </Box>
          )}
        </Stack>
      </form>
    </div>
  );
};
export default SetupTemplate;
